import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaStar, FaRegStar, FaIndustry, FaUpload, FaImages } from 'react-icons/fa';

const emptyForm = {
  category_id: '', name: '', slug: '', short_description: '',
  full_description: '', main_image: '', is_featured: false,
  is_active: 1, display_order: 0,
  specifications: [],
  gallery_images: []
};

export default function AdminProducts() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');

  const load = () => {
    axios.get('/api/products/all', { headers }).then(r => setProducts(r.data)).catch(() => {});
    axios.get('/api/categories/all', { headers }).then(r => setCategories(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const autoSlug = (name) => name.toLowerCase()
    .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const addSpec = () => setForm(f => ({ ...f, specifications: [...f.specifications, { key: '', value: '' }] }));
  const removeSpec = (i) => setForm(f => ({ ...f, specifications: f.specifications.filter((_, idx) => idx !== i) }));
  const updateSpec = (i, field, val) => setForm(f => ({
    ...f, specifications: f.specifications.map((s, idx) => idx === i ? { ...s, [field]: val } : s)
  }));
  const removeGallery = (i) => setForm(f => ({ ...f, gallery_images: f.gallery_images.filter((_, idx) => idx !== i) }));

  const openAdd = () => { setForm(emptyForm); setEditId(null); setError(''); setModal(true); };
  const openEdit = async (p) => {
    let specs = [];
    if (p.specifications) {
      try {
        const parsed = typeof p.specifications === 'string' ? JSON.parse(p.specifications) : p.specifications;
        if (Array.isArray(parsed)) {
          specs = parsed.map(s => typeof s === 'object' && s.key !== undefined ? s : { key: String(s), value: '' });
        } else if (typeof parsed === 'object') {
          specs = Object.entries(parsed).map(([key, value]) => ({ key, value: String(value) }));
        }
      } catch {}
    }
    let galleryImages = [];
    try {
      const { data } = await axios.get(`/api/products/${p.id}/images`, { headers });
      galleryImages = data.map(img => img.image_url);
    } catch {}
    setForm({
      ...p, is_featured: !!p.is_featured, specifications: specs,
      gallery_images: galleryImages, main_image: p.main_image || '', category_id: p.category_id || ''
    });
    setEditId(p.id); setError(''); setModal(true);
  };
  const close = () => { setModal(false); setError(''); };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('image', file);
    try {
      const { data } = await axios.post('/api/upload', fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, main_image: data.url }));
    } catch { alert('Yükleme başarısız'); }
    setUploading(false);
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const urls = [];
    for (const file of files) {
      const fd = new FormData(); fd.append('image', file);
      try {
        const { data } = await axios.post('/api/upload', fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
        urls.push(data.url);
      } catch {}
    }
    setForm(f => ({ ...f, gallery_images: [...f.gallery_images, ...urls] }));
    setUploading(false);
    e.target.value = '';
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    const payload = {
      ...form,
      specifications: form.specifications.filter(s => s.key.trim()),
      gallery_images: form.gallery_images
    };
    try {
      if (editId) await axios.put(`/api/products/${editId}`, payload, { headers });
      else await axios.post('/api/products', payload, { headers });
      load(); close();
    } catch (err) { setError(err.response?.data?.message || 'Hata oluştu'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;
    await axios.delete(`/api/products/${id}`, { headers });
    load();
  };

  const handleToggle = async (id) => {
    await axios.patch(`/api/products/${id}/toggle`, {}, { headers });
    load();
  };

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.category_name || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-primary">Ürünler</h1>
          <p className="text-gray-500 text-sm">{products.length} ürün</p>
        </div>
        <button onClick={openAdd} className="btn-primary text-sm py-2.5 flex-shrink-0">
          <FaPlus /> Ürün Ekle
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-4 border-b">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Ürün ara..." className="input-field max-w-xs text-sm py-2" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-5 py-3 text-left font-medium text-gray-500">Ürün</th>
                <th className="px-5 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Kategori</th>
                <th className="px-5 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">Öne Çıkan</th>
                <th className="px-5 py-3 text-left font-medium text-gray-500">Durum</th>
                <th className="px-5 py-3 text-right font-medium text-gray-500">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {p.main_image ? <img src={p.main_image} alt="" className="w-full h-full object-cover" /> : <FaIndustry className="text-gray-300" />}
                      </div>
                      <span className="font-medium text-gray-700">{p.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell">{p.category_name || '—'}</td>
                  <td className="px-5 py-3.5 hidden sm:table-cell">
                    {p.is_featured ? <FaStar className="text-secondary" /> : <FaRegStar className="text-gray-300" />}
                  </td>
                  <td className="px-5 py-3.5">
                    <button onClick={() => handleToggle(p.id)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${p.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                      {p.is_active ? 'Aktif' : 'Pasif'}
                    </button>
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(p)} className="p-2 text-gray-500 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors">
                        <FaEdit size={14} />
                      </button>
                      <button onClick={() => handleDelete(p.id)} className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="text-center py-12 text-gray-400">Ürün bulunamadı</div>}
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
              <h3 className="font-semibold text-gray-700">{editId ? 'Ürün Düzenle' : 'Yeni Ürün'}</h3>
              <button onClick={close} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Ürün Adı *</label>
                  <input value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: !editId ? autoSlug(e.target.value) : f.slug }))}
                    className="input-field" placeholder="Ürün adı" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">URL Kodu *
                    <span className="text-gray-400 font-normal text-xs ml-1">(addan otomatik oluşur)</span>
                  </label>
                  <input value={form.slug} readOnly
                    className="input-field font-mono text-sm bg-gray-50 cursor-default text-gray-500" placeholder="urun-url-kodu" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Kategori</label>
                  <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))} className="input-field">
                    <option value="">Kategori Seçin</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Sıra / Durum</label>
                  <div className="flex gap-2">
                    <input type="number" value={form.display_order} onChange={e => setForm(f => ({ ...f, display_order: +e.target.value }))}
                      className="input-field w-24" placeholder="0" />
                    <select value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: +e.target.value }))} className="input-field flex-1">
                      <option value={1}>Aktif</option>
                      <option value={0}>Pasif</option>
                    </select>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Kısa Açıklama</label>
                <textarea value={form.short_description} onChange={e => setForm(f => ({ ...f, short_description: e.target.value }))}
                  rows={2} className="input-field resize-none" placeholder="Kısa açıklama" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Tam Açıklama</label>
                <textarea value={form.full_description} onChange={e => setForm(f => ({ ...f, full_description: e.target.value }))}
                  rows={4} className="input-field resize-none" placeholder="Detaylı açıklama" />
              </div>

              {/* Ana Görsel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Ana Görsel</label>
                <div className="flex gap-2">
                  <input value={form.main_image} onChange={e => setForm(f => ({ ...f, main_image: e.target.value }))}
                    className="input-field flex-1" placeholder="URL veya dosya yükleyin" />
                  <label className="btn-secondary text-sm py-3 px-4 cursor-pointer flex-shrink-0">
                    {uploading ? '...' : <><FaUpload size={13} /> Yükle</>}
                    <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                  </label>
                </div>
                {form.main_image && (
                  <img src={form.main_image} alt="" className="mt-2 h-20 rounded-lg object-cover" />
                )}
              </div>

              {/* Galeri Görseller */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    <FaImages className="inline mr-1.5" size={14} />
                    Galeri Görseller
                    <span className="text-gray-400 font-normal text-xs ml-1">({form.gallery_images.length} görsel)</span>
                  </label>
                  <label className="text-xs text-secondary hover:text-secondary font-medium cursor-pointer flex items-center gap-1">
                    <FaPlus size={10} /> Görsel Ekle
                    <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} className="hidden" />
                  </label>
                </div>
                {form.gallery_images.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {form.gallery_images.map((url, i) => (
                      <div key={i} className="relative group">
                        <img src={url} alt="" className="w-20 h-20 object-cover rounded-lg border border-gray-200" />
                        <button type="button" onClick={() => removeGallery(i)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                          <FaTimes size={9} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center text-sm text-gray-400">
                    Ek görsel yok — yukarıdan ekleyebilirsiniz
                  </div>
                )}
              </div>

              {/* Teknik Özellikler */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Teknik Özellikler</label>
                  <button type="button" onClick={addSpec}
                    className="text-xs text-secondary font-medium flex items-center gap-1 hover:underline">
                    <FaPlus size={10} /> Satır Ekle
                  </button>
                </div>
                {form.specifications.length > 0 ? (
                  <div className="space-y-2">
                    {form.specifications.map((spec, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input
                          value={spec.key}
                          onChange={e => updateSpec(i, 'key', e.target.value)}
                          placeholder="Özellik (örn: Ağırlık)"
                          className="input-field flex-1 text-sm py-2"
                        />
                        <input
                          value={spec.value}
                          onChange={e => updateSpec(i, 'value', e.target.value)}
                          placeholder="Değer (örn: 25 kg)"
                          className="input-field flex-1 text-sm py-2"
                        />
                        <button type="button" onClick={() => removeSpec(i)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0">
                          <FaTimes size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-3 text-center text-sm text-gray-400">
                    Özellik yok — "Satır Ekle" ile ekleyebilirsiniz
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))}
                  className="w-4 h-4 accent-secondary" />
                <span className="text-sm text-gray-700">Öne çıkan ürün olarak işaretle</span>
              </label>
              {error && <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-2 text-sm">{error}</div>}
            </div>
            <div className="flex gap-3 px-6 pb-6 flex-shrink-0">
              <button onClick={close} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm font-medium">İptal</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary justify-center py-2.5 text-sm disabled:opacity-60">
                {saving ? 'Kaydediliyor...' : <><FaSave size={13} /> Kaydet</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
